import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Spin } from "antd";
import { Brush, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { debounce, round } from "lodash";
import "./SentimentOverTimeLineGraph.css";
import { LoadingOutlined } from "@ant-design/icons";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import { useGetSentimentsBreakdownMutation } from "../../../store/api/lumi/sentiment";
import moment from "moment/moment";
import "../../../components/Widgets/AllSentimentBarsOverTime/AllSentimentBarsOverTime.css";
import { formatDateToUtc } from "../../../utils/formatDate";
import { useSelector } from "react-redux";
import { getLineColor } from "../../../utils/getLineColor";

export function SentimentOverTimeLineGraph(props) {
    const {
        id,
        filters,
        includeExamples,
        selectedDateName,
        interval,
        setFilterDocCount,
        widget,
        smoothLine
    } = props;

    const [chartData, setChartData] = useState([]);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [getSentimentsBreakdown] = useGetSentimentsBreakdownMutation();
    const [defaultConcepts, setDefaultConcepts] = useState([]);
    const conceptFilter = useMemo(() => JSON.stringify(getConceptFilter(props)), [props]);
    const [conceptColors, setConceptColors] = useState({});

    const labelFontSize = useSelector((state) => {
        const fontSizes = state.fontManager.fontSizes;
        const normalizedKey = Object.keys(fontSizes).find(
            (key) => key.trim() === "labelFontSize",
        );
        return fontSizes[normalizedKey]  || 16;
    });

    const removeEffectSuffix = (label) => label.replace(/_effect$/, '');

    const getSentimentData = useCallback(async () => {
        setLoading(true);

        const {data} = await getSentimentsBreakdown({
            id,
            concept_selector: JSON.parse(conceptFilter),
            filter: filters,
            include_examples: includeExamples,
            breakdowns: [{name: selectedDateName, interval}],
        });
        setFilterDocCount(data.filter_count);
        setData(data.breakdowns?.[0].buckets);
        const uniqueConcepts = data.match_counts
            .map((item) => item.name)
            .slice(0, JSON.parse(conceptFilter).limit || 10);
        setDefaultConcepts(uniqueConcepts);
        setLoading(false);
    }, [
        getSentimentsBreakdown,
        id,
        filters,
        includeExamples,
        interval,
        selectedDateName,
        conceptFilter,
        setFilterDocCount,
        props.conceptTypeDay,
        props.conceptList,
    ]);

    useEffect(() => {
        setLoading(true)

        const debouncedGetSentiment = debounce(getSentimentData, 1000);

        debouncedGetSentiment();


        return () => {
            debouncedGetSentiment.cancel();
        };
    }, [getSentimentData]);

    function calculateSentimentEffect(negative, neutral, positive) {

        const weightNegative = -1;
        const weightNeutral = 0;
        const weightPositive = 1;

        const sentimentEffect =
            negative * weightNegative +
            neutral * weightNeutral +
            positive * weightPositive;

        return Math.trunc(sentimentEffect * 100) / 100;
    }

    useEffect(() => {
        if (data?.length) {
            const conceptColors = {};
            const preparedData = data.map(({label, match_counts, filter_count}) => {
                if (!filter_count) return

                const formattedDate = formatDateToUtc(label, props.interval);

                const dateData = {iteration: formattedDate};
                match_counts.forEach((item) => {
                    if (item.color) conceptColors[item.name] = item.color;
                    dateData[`${item.name}_effect`] = calculateSentimentEffect(
                        item.sentiment_share.negative,
                        item.sentiment_share.neutral,
                        item.sentiment_share.positive
                    );
                    dateData[`${item.name}_positive`] =
                        item.sentiment_share.positive || 0;
                    dateData[`${item.name}_neutral`] =
                        item.sentiment_share.neutral || 0;
                    dateData[`${item.name}_negative`] =
                        0 - item.sentiment_share.negative || 0;
                    dateData[`${item.name}`] = item.sentiment_counts.total || 0;
                });

                return dateData;
            });
            setConceptColors(conceptColors);
            setChartData(
                preparedData?.filter(value => value !== undefined).sort((e, f) =>
                    moment(e.iteration).isBefore(moment(f.iteration)) ? -1 : 1
                )
            );
        }
    }, [data]);

    const CustomizedLabel = ({x, y, stroke, value, active}) => {
        return active ? (
            <text
                key={x + y}
                x={x}
                y={y}
                dy={-4}
                fill={stroke}
                fontSize={10}
                textAnchor="middle"
            >
                {JSON.stringify(round(value, 2))}
            </text>
        ) : null;
    };

    return (
        <div className={"volume-compare"}>
            <Spin
                spinning={loading}
                indicator={<LoadingOutlined className={'spinner-by-center'} style={{fontSize: 24}} spin/>}
            >
                <div
                    className="sentiment-bars-container"
                    style={{height: widget.layout.h * 39 - 150}}
                    onMouseDown={(e) => {
                        if (e.target.closest(".recharts-brush")) {
                            e.stopPropagation();
                            e.preventDefault()
                        }
                    }}
                >
                    <ResponsiveContainer height="100%" minHeight={70}>
                        <LineChart data={chartData}  margin={{
                            top: 10,
                            right: 10,
                            left: 50,
                            bottom: 0,
                        }}>
                            <CartesianGrid strokeDasharray="3 3"/>
                            <Legend
                                wrapperStyle={{
                                    fontSize: `${labelFontSize}px`,
                                    fontWeight: "bold",
                                    color: "#000",
                                }}
                            />

                            <XAxis
                                allowDataOverflow
                                domain={["auto", "auto"]}
                                dataKey="iteration"
                                tick={{ fontSize: 16, color: "#000", fontWeight: "bold" }}
                            >
                                <text
                                    x={500}
                                    y={50}
                                    dy={30}
                                    textAnchor="middle"
                                    fontSize={16}
                                    fontWeight={600}
                                    color={'#000'}
                                >
                                    Date
                                </text>
                            </XAxis>
                            <YAxis
                                type="number"
                                label={{
                                    value: "Net Sentiment",
                                    angle: -90,
                                    dx: -30,
                                    position: "insideLeft",
                                    style: { textAnchor: "middle", fontSize: 16, color: "#000", fontWeight: "bold" },
                                }}
                                domain={[-1.5, 1.5]}
                                tick={{ fontSize: 16, color: "#000", fontWeight: "bold" }}
                                allowDataOverflow
                            />

                            <Tooltip
                                formatter={(value, name) => [value, removeEffectSuffix(name)]}
                            />

                            {props.selectedLocalConcepts.length > 0
                                ? Array.from(props.selectedLocalConcepts).map((concept, index) => {
                                    return (
                                        <Fragment key={`${concept}_sentiment_${index}`}>
                                            <Line
                                                dot={!smoothLine}
                                                strokeWidth={3}
                                                name={removeEffectSuffix(concept)}
                                                type={smoothLine ? "monotone" : "linear"}
                                                dataKey={`${concept}_effect`}
                                                stroke={conceptColors[concept] || getLineColor(index)}
                                                fill={conceptColors[concept] || getLineColor(index)}
                                                stackId="positive"
                                                activeDot={false}
                                                label={CustomizedLabel}
                                                connectNulls
                                            />
                                        </Fragment>
                                    );
                                })
                                : Array.from(defaultConcepts).map((concept, index) => {
                                    return (
                                        <Fragment key={`${concept}_sentiment_${index}`}>
                                            <Line
                                                dot={!smoothLine}
                                                type={smoothLine ? "monotone" : "linear"}
                                                dataKey={`${concept}_effect`}
                                                strokeWidth={3}
                                                name={removeEffectSuffix(concept)}
                                                stroke={conceptColors[concept] || getLineColor(index)}
                                                fill={conceptColors[concept] || getLineColor(index)}
                                                stackId="positive"
                                                activeDot={false}
                                                label={CustomizedLabel}
                                                connectNulls
                                            />
                                        </Fragment>
                                    );
                                })}
                            <Brush
                                dataKey="date"
                                height={30}
                                stroke="#696969"
                                travellerWidth={12}
                                gap={2}
                                startIndex={0}
                                endIndex={chartData.length - 1}
                                fill="#D3D3D3"
                                travellerStyle={{
                                    stroke: "#696969",
                                    fill: "#A9A9A9",
                                    cursor: "pointer",
                                }}
                                selectedBoxStyle={{
                                    stroke: "#A9A9A9",
                                    fill: "rgba(169, 169, 169, 0.2)",
                                    cursor: "move",
                                }}
                                padding={{ top: 2, bottom: 2 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </Spin>
        </div>
    );
}
